import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Handshake, ArrowRight, LogIn, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }),
};

export default function PartnerPortal() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter credentials."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      navigate("/partner/dashboard");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Invalid credentials");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { toast.error("Please fill all required fields."); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { name, email, password, phone: phone || null }, { withCredentials: true });
      toast.success("Partner account created.");
      navigate("/partner/dashboard");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Registration failed.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="partner-portal-page" className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
      <motion.div initial="hidden" animate="visible" className="w-full max-w-md px-6">
        <motion.div variants={fadeUp} custom={0} className="text-center mb-10">
          <Handshake size={36} className="text-gold mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-white mb-2">Partner Portal</h1>
          <p className="text-white/40 font-body text-sm">Feature your brand on our exclusive platform</p>
        </motion.div>

        <motion.div variants={fadeUp} custom={1} className="flex mb-8">
          <button data-testid="partner-login-tab" onClick={() => setMode("login")} className={`flex-1 py-3 text-sm font-body text-center transition-all border-b-2 ${mode === "login" ? "border-gold text-gold" : "border-white/10 text-white/40"}`}>
            <LogIn size={16} className="inline mr-2" />Sign In
          </button>
          <button data-testid="partner-register-tab" onClick={() => setMode("register")} className={`flex-1 py-3 text-sm font-body text-center transition-all border-b-2 ${mode === "register" ? "border-gold text-gold" : "border-white/10 text-white/40"}`}>
            <UserPlus size={16} className="inline mr-2" />Register
          </button>
        </motion.div>

        {mode === "login" ? (
          <motion.form key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={handleLogin} className="space-y-5" data-testid="partner-login-form">
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Email</label>
              <Input data-testid="partner-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="company@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
            </div>
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Password</label>
              <Input data-testid="partner-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
            </div>
            <button type="submit" disabled={loading} data-testid="partner-login-submit" className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-50">
              {loading ? "Signing in..." : "Access Portal"} <ArrowRight size={16} />
            </button>
          </motion.form>
        ) : (
          <motion.form key="register" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={handleRegister} className="space-y-5" data-testid="partner-register-form">
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Company / Name *</label>
              <Input data-testid="partner-reg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Company or your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
            </div>
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Email *</label>
              <Input data-testid="partner-reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="company@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
            </div>
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Phone</label>
              <Input data-testid="partner-reg-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (876) 000-0000" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
            </div>
            <div>
              <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Password * (min 8 characters)</label>
              <Input data-testid="partner-reg-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a password" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
            </div>
            <button type="submit" disabled={loading} data-testid="partner-register-submit" className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-50">
              {loading ? "Creating Account..." : "Create Partner Account"} <ArrowRight size={16} />
            </button>
          </motion.form>
        )}

        <motion.div variants={fadeUp} custom={3} className="glass-card p-5 mt-8">
          <h4 className="text-white font-body text-xs font-medium mb-2">Why Partner With Us?</h4>
          <ul className="text-white/40 text-xs font-body space-y-1">
            <li>Reach VIP and high-net-worth audiences</li>
            <li>Featured placement on our premium platform</li>
            <li>Tiers starting from $250/week</li>
          </ul>
        </motion.div>
      </motion.div>
    </div>
  );
}
